import { HTMLAttributes, useState } from 'react';
import clsx from 'clsx';
import {
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

export type AlertVariant = 'info' | 'success' | 'warning' | 'error';

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant;
  title?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  icon?: React.ReactNode;
  showIcon?: boolean;
}

const variantStyles: Record<AlertVariant, string> = {
  info: 'bg-blue-50 border-blue-200 text-blue-800',
  success: 'bg-green-50 border-green-200 text-green-800',
  warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
  error: 'bg-red-50 border-red-200 text-red-800',
};

const iconStyles: Record<AlertVariant, string> = {
  info: 'text-blue-400',
  success: 'text-green-400',
  warning: 'text-yellow-400',
  error: 'text-red-400',
};

const defaultIcons: Record<AlertVariant, React.ReactNode> = {
  info: <InformationCircleIcon className="h-5 w-5" />,
  success: <CheckCircleIcon className="h-5 w-5" />,
  warning: <ExclamationTriangleIcon className="h-5 w-5" />,
  error: <XCircleIcon className="h-5 w-5" />,
};

export const Alert = ({
  variant = 'info',
  title,
  dismissible = false,
  onDismiss,
  icon,
  showIcon = true,
  children,
  className,
  ...props
}: AlertProps) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  if (!isVisible) {
    return null;
  }

  const displayIcon = icon || defaultIcons[variant];

  return (
    <div
      role="alert"
      className={clsx(
        'rounded-lg border p-4',
        variantStyles[variant],
        className
      )}
      {...props}
    >
      <div className="flex">
        {showIcon && (
          <div className={clsx('flex-shrink-0', iconStyles[variant])}>
            {displayIcon}
          </div>
        )}

        <div className={clsx('flex-1', showIcon && 'ml-3')}>
          {title && (
            <h3 className="text-sm font-medium mb-1">
              {title}
            </h3>
          )}
          {children && (
            <div className="text-sm">
              {children}
            </div>
          )}
        </div>

        {dismissible && (
          <div className="flex-shrink-0 ml-3">
            <button
              onClick={handleDismiss}
              className={clsx(
                'inline-flex rounded-md p-1.5',
                'hover:bg-black hover:bg-opacity-10',
                'focus:outline-none focus:ring-2 focus:ring-offset-2',
                iconStyles[variant]
              )}
              aria-label="Dismiss alert"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
